import type { ConversationMessage, MessageReaction } from '@/lib/gupshup-client';

export type MediaData = {
  url: string;
  contentType?: string;
  filename?: string;
};

/** UI-shaped message consumed by MessageBubble/MessageContent (built by transformMessages). */
export type Message = {
  id: string;
  direction: 'inbound' | 'outbound';
  content: string;
  createdAt: string;
  status?: string;
  phoneNumber: string;
  hasMedia: boolean;
  mediaData?: MediaData;
  reactions?: { customer?: MessageReaction; business?: MessageReaction };
  filename?: string | null;
  mimeType?: string | null;
  messageType?: string;
  caption?: string | null;
  metadata?: {
    mediaId?: string;
    caption?: string;
    role?: 'human' | 'assistant';
    externalId?: string;
    location?: {
      latitude: number;
      longitude: number;
    };
  };
  interactiveButtons?: Array<{ id: string; title: string }>;
  interactiveCtaButton?: { text: string; url: string };
  isLocationRequest?: boolean;
  interactiveFlow?: { bodyText?: string; ctaText?: string };
  /** Incoming list-picker reply (interactive list_reply). */
  listReply?: { id?: string; title?: string; description?: string };
  /** Incoming WhatsApp Flow completion (interactive nfm_reply). */
  nfmReply?: { name?: string; body?: string; responseJson?: unknown; responseJsonRaw?: string };
  /** Shared contact cards (contacts message). */
  contacts?: Array<{ name?: string; phones?: Array<{ phone?: string; type?: string }>; emails?: string[] }>;
  /** Outgoing template message details. */
  template?: { templateId?: string; params?: unknown[]; mediaUrl?: string };
};

const MEDIA_TYPES = ['image', 'audio', 'video', 'document', 'sticker'];

/** Map a raw API message into the UI Message shape. */
export function toUiMessage(msg: ConversationMessage): Message {
  const meta = msg.externalMetadata;
  const hasMediaType = MEDIA_TYPES.includes(msg.type);
  const mediaUrl = hasMediaType ? meta?.media?.url : undefined;
  const mediaCaption = hasMediaType ? meta?.media?.caption : undefined;
  const mimeType = hasMediaType ? meta?.media?.mimeType : undefined;

  const locationData = msg.type === 'location' ? meta?.location : undefined;

  const isInteractive = msg.type === 'interactive';
  const interactiveSubtype = isInteractive ? meta?.type : undefined;
  const interactiveButtons =
    isInteractive && Array.isArray(meta?.buttons)
      ? (meta?.buttons as Array<{ id: string; title: string }>)
      : undefined;
  const interactiveCtaButton =
    isInteractive && meta?.button
      ? {
          text: meta.button.text as string,
          url: (meta.button.actionUrl || meta.button.url) as string,
        }
      : undefined;
  const isTemplate = interactiveSubtype === 'template' || (msg.type !== 'interactive' && meta?.type === 'template');

  return {
    id: msg.id,
    direction: msg.direction === 'outgoing' ? 'outbound' : 'inbound',
    content: msg.content,
    createdAt: msg.createdAt,
    status: msg.status,
    phoneNumber: '',
    hasMedia: hasMediaType && !!mediaUrl,
    mediaData: hasMediaType && mediaUrl ? { url: mediaUrl, contentType: mimeType } : undefined,
    messageType: msg.type || 'text',
    caption: mediaCaption,
    reactions: meta?.reactions,
    metadata: {
      role: msg.role,
      externalId: msg.externalId,
      location: locationData,
    },
    interactiveButtons,
    interactiveCtaButton,
    isLocationRequest: interactiveSubtype === 'location_request_message',
    interactiveFlow:
      interactiveSubtype === 'flow' ? { bodyText: meta?.bodyText, ctaText: meta?.ctaText } : undefined,
    listReply: isInteractive && meta?.listReply ? meta.listReply : undefined,
    nfmReply: interactiveSubtype === 'nfm_reply' && meta?.nfmReply ? meta.nfmReply : undefined,
    contacts: meta?.contacts,
    template: isTemplate
      ? { templateId: meta?.templateId, params: meta?.params, mediaUrl: meta?.mediaUrl }
      : undefined,
  };
}
